import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import Autocomplete from "@/components/ui/autocomplete";
import { Label } from "@/components/ui/label";

import { FilterState } from "@/hooks/useFilters";
import { useGovtOrganizationLevel } from "@/hooks/useGovtOrganizationLevel";

import { Organization } from "@/types/organization/organization";

interface GovtOrganizationSelectorProps {
  value?: string;
  onChange: (value: string) => void;
  required?: boolean;
  requiredDepth?: number;
  authToken?: string;
  selected?: Organization[];
  className?: string;
  levelClassName?: string;
  errorMessage?: string; 

  ref?: React.RefCallback<HTMLButtonElement | null>;

  "aria-invalid"?: boolean;
}

interface OrganizationLevelProps {
  index: number;
  currentLevel?: Organization;
  previousLevel?: Organization;
  onChange: (
    filter: FilterState,
    index: number,
    organization: Organization,
  ) => void;
  required?: boolean;
  authToken?: string;
  className?: string;
  errorMessage?: string;

  isError?: boolean;

  ref?: React.RefCallback<HTMLButtonElement | null>;
}

function OrganizationLevelSelect({
  index,
  currentLevel,
  previousLevel,
  onChange,
  required,
  authToken,
  className,
  isError,
  errorMessage,
  ref,
}: OrganizationLevelProps) {
  const { t } = useTranslation();

  const parentId = index === 0 ? "" : previousLevel?.id || "";

  const { options, handleChange, handleSearch, organizations, isFetching } =
    useGovtOrganizationLevel({
      index,
      onChange: (filter: FilterState, index: number) => {
        const selectedOrg = organizations?.find(
          (org) => org.id === filter.organization,
        );
        if (selectedOrg) {
          onChange(filter, index, selectedOrg);
        }
      },
      parentId,
      authToken,
    });

  return (
    <div className={cn("mt-2 flex flex-col gap-1.5 w-full", className)}>
      <Label>
        {t(
          currentLevel
            ? `SYSTEM__govt_org_type__${currentLevel.metadata?.govt_org_type}`
            : index === 0
              ? "SYSTEM__govt_org_type__default"
              : `SYSTEM__govt_org_type__${previousLevel?.metadata?.govt_org_children_type || "default"}`,
        )}
        {required && <span className="ml-1 text-red-500">*</span>}
      </Label>

      <div className="flex items-center gap-2 w-full">
        {isFetching && <Loader2 className="size-5 animate-spin text-muted-foreground shrink-0" />}
        <div className="w-full">
          <Autocomplete
            showClearButton={false}
            aria-invalid={isError}
            ref={ref}
            value={currentLevel?.id || ""}
            options={options}
            onChange={handleChange}
            onSearch={handleSearch}
          />
        </div>
      </div>

      {/* Error message ab hamesha input box ke NICHE show hoga */}
      {isError && errorMessage && (
        <p className="text-sm font-medium text-red-500 mt-1">
          {errorMessage}
        </p>
      )}
    </div>
  );
}

export default function GovtOrganizationSelector({
  onChange,
  required,
  selected,
  authToken,
  requiredDepth,
  className,
  levelClassName,
  errorMessage,
  ...props
}: GovtOrganizationSelectorProps) {
  const [selectedLevels, setSelectedLevels] = useState(selected || []);

  useEffect(() => {
    if (requiredDepth != null) {
      if (selectedLevels.length < requiredDepth) {
        onChange("");
      }
      return;
    }

    if (required && selectedLevels[selectedLevels.length - 1]?.has_children) {
      onChange("");
      return;
    }
  }, [selectedLevels]);

  useEffect(() => {
    if (selected && selected.length > 0) {
      let currentOrg = selected[0];
      if (currentOrg?.level_cache === 0) {
        setSelectedLevels(selected);
      } else {
        const levels: Organization[] = [];
        while (currentOrg && currentOrg.level_cache >= 0) {
          levels.unshift(currentOrg);
          currentOrg = currentOrg.parent as unknown as Organization;
        }
        setSelectedLevels(levels);
      }
    }
  }, [selected]);

  useEffect(() => {
    const selectedLeaf = selectedLevels[selectedLevels.length - 1];
    if (!selectedLeaf) {
      return;
    }

    if (props.value === selectedLeaf.id) {
      return;
    }

    if (requiredDepth != null) {
      if (selectedLevels.length >= requiredDepth) {
        onChange(selectedLeaf.id);
      }
      return;
    }

    if (!required || !selectedLeaf.has_children) {
      onChange(selectedLeaf.id);
    }
  }, [onChange, required, requiredDepth, selectedLevels, props.value]);

  const handleFilterChange = (
    filter: FilterState,
    index: number,
    organization: Organization,
  ) => {
    if (filter.organization) {
      setSelectedLevels((prev) => {
        const newLevels = prev.slice(0, index);
        newLevels.push(organization);
        return newLevels;
      });
      if (requiredDepth != null && index + 1 >= requiredDepth) {
        onChange(organization.id);
      } else if (!required || (required && !organization.has_children)) {
        onChange(organization.id);
      } else {
        onChange("");
      }
    } else {
      onChange("");
      setSelectedLevels((prev) => prev.slice(0, index));
    }
  };

  const totalLevels =
    selectedLevels.length +
    (selectedLevels.length === 0 ||
    selectedLevels[selectedLevels.length - 1]?.has_children
      ? 1
      : 0);

  const levels = Array.from({ length: totalLevels }).map((_, index) => {
    const isErrorLevel =
      !!props["aria-invalid"] &&
      (requiredDepth != null ? index < requiredDepth : required) &&
      !selectedLevels[index];

    return (
      <OrganizationLevelSelect
        isError={isErrorLevel}
        errorMessage={isErrorLevel ? errorMessage : undefined}
        key={index}
        ref={props.ref}
        className={levelClassName}
        index={index}
        currentLevel={selectedLevels[index]}
        previousLevel={selectedLevels[index - 1]}
        onChange={handleFilterChange}
        required={requiredDepth != null ? index < requiredDepth : required}
        authToken={authToken}
      />
    );
  });

  return (
    <div className={cn("grid grid-cols-1 md:grid-cols-2 gap-4 w-full", className)}>
      {levels}
    </div>
  );
}
